// Pure node HTML renderers for Drawflow nodes.
// No state dependencies — callers inject subprocessList and motionTimes where needed.

function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]));
}

function numOrNull(v) {
    if (v === "" || v == null) return null;
    const n = parseFloat(v);
    return isFinite(n) ? n : null;
}

function numOrZero(v) {
    const n = numOrNull(v);
    return n == null ? 0 : n;
}

function renderNodeTypeLabel(label) {
    return `<div class="node-type">${escapeHtml(label)}</div>`;
}

export function getNodeData(node) {
    return node?.data || {};
}

export function getNodeTypeFromData(data) {
    return data?.nodeType || (data?.motionId ? "motion" : "");
}

export function makeStartNodeData() {
    return { nodeType: "start", label: "開始" };
}

export function makeButtonNodeData() {
    return {
        nodeType: "button",
        label: "押しボタン",
        signalName: "X_PB1",
        mode: "momentary",
        trueLabel: "ON",
        falseLabel: "OFF"
    };
}

export function makeConditionNodeData() {
    return {
        nodeType: "condition",
        conditionText: "条件",
        conditionMode: "single",
        operator: "AND",
        conditions: [{ label: "条件", expression: "条件" }],
        trueLabel: "ON",
        falseLabel: "OFF",
        timeoutLabel: "TIMEOUT",
        timeoutSec: 5
    };
}

export function makeErrorNodeData() {
    return {
        nodeType: "error",
        errorCode: "E001",
        errorMessage: "条件タイムアウト"
    };
}

export function makeSignalOutputNodeData() {
    return {
        nodeType: "signal_output",
        label: "ランプ点灯",
        signalScope: "external",
        signalName: "GreenLamp",
        action: "ON"
    };
}

export function makeWaitNodeData() {
    return {
        nodeType: "wait",
        label: "待機",
        durationSec: 1
    };
}

export function makeEndNodeData() {
    return {
        nodeType: "end",
        label: "終了",
        result: "normal"
    };
}

export function getFlowNodeLabelFromData(
    data,
    node = null,
    { findMotion = () => null } = {}
) {
    const nodeType = getNodeTypeFromData(data);
    if (nodeType === "motion" && data?.motionId) {
        const found = findMotion(data.motionId);
        if (found) return found.motion.name || found.axis.name || "動作";
    }
    if (nodeType === "condition") return data?.conditionText || "条件";
    if (nodeType === "button") return data?.label || data?.signalName || "操作入力";
    if (nodeType === "error") return data?.errorCode || data?.errorMessage || "エラー";
    if (nodeType === "signal_output") return data?.label || data?.signalName || "信号操作";
    if (nodeType === "wait") return data?.label || "待機";
    if (nodeType === "subprocess") return data?.subprocessName || data?.label || "サブプロセス";
    if (nodeType === "end") return data?.label || "終了";
    if (nodeType === "start") return data?.label || "開始";
    return node?.name || "ノード";
}

export function normalizeConditionEntry(entry = {}, index = 0, fallbackName = "条件") {
    const baseLabel = index === 0 ? fallbackName : `条件${index + 1}`;
    const label = String(entry?.label || baseLabel).trim() || baseLabel;
    const expression = String(entry?.expression || label).trim() || label;
    return { label, expression };
}

export function getConditionMode(data = {}) {
    return data?.conditionMode === "composite" ? "composite" : "single";
}

export function getConditionOperator(data = {}) {
    return data?.operator === "OR" ? "OR" : "AND";
}

export function normalizeConditionEntries(entries = [], fallbackName = "条件", mode = "single") {
    const source = Array.isArray(entries) ? entries : [];
    const normalized = source
        .map((entry, index) => normalizeConditionEntry(entry, index, fallbackName))
        .filter((entry) => entry.label || entry.expression);
    if (!normalized.length) {
        normalized.push(normalizeConditionEntry({ label: fallbackName, expression: fallbackName }, 0, fallbackName));
    }
    return mode === "composite" ? normalized : [normalized[0]];
}

export function getConditionKind(data = {}) {
    if (data.conditionKind === "signal" || data.conditionKind === "internalFlag") return data.conditionKind;
    return "expression";
}

export function ensureConditionNodeData(data = {}) {
    const conditionText = String(data.conditionText || "条件").trim() || "条件";
    const conditionMode = getConditionMode(data);
    data.nodeType = "condition";
    data.conditionText = conditionText;
    data.conditionMode = conditionMode;
    data.operator = getConditionOperator(data);
    data.conditions = normalizeConditionEntries(data.conditions, conditionText, conditionMode);
    if (!data.trueLabel) data.trueLabel = "ON";
    if (!data.falseLabel) data.falseLabel = "OFF";
    if (!data.timeoutLabel) data.timeoutLabel = "TIMEOUT";
    if (!Number.isFinite(Number(data.timeoutSec)) || Number(data.timeoutSec) < 0) data.timeoutSec = 5;
    return data;
}

export function buildConditionNodeMeta(data = {}) {
    const normalized = ensureConditionNodeData(data);
    if (normalized.conditionMode !== "composite") return "";
    const labels = normalized.conditions.map((item) => item.label).filter(Boolean);
    const preview = labels.slice(0, 2).join(" / ");
    const suffix = labels.length > 2 ? ` +${labels.length - 2}` : "";
    return `${normalized.operator}: ${labels.length}条件${preview ? ` (${preview}${suffix})` : ""}`;
}

export function normalizeSignalScope(value) {
    return value === "internal_flag" ? "internal_flag" : "external";
}

export function ensureSignalOutputData(data = {}) {
    data.nodeType = "signal_output";
    data.signalScope = normalizeSignalScope(data.signalScope);
    data.label = String(data.label || "信号操作").trim() || "信号操作";
    data.signalName = String(data.signalName || (data.signalScope === "internal_flag" ? "InternalFlag" : "Signal")).trim()
        || (data.signalScope === "internal_flag" ? "InternalFlag" : "Signal");
    if (data.signalScope === "internal_flag") {
        if (!["SET", "RESET"].includes(String(data.action || ""))) data.action = "SET";
    } else if (!["ON", "OFF", "BLINK"].includes(String(data.action || ""))) {
        data.action = "ON";
    }
    return data;
}

export function ensureWaitNodeData(data = {}) {
    data.nodeType = "wait";
    data.label = String(data.label || "待機").trim() || "待機";
    const durationSec = numOrNull(data.durationSec);
    data.durationSec = durationSec != null && durationSec >= 0 ? durationSec : 1;
    return data;
}

export function normalizeButtonMode(value) {
    return value === "alternate" ? "alternate" : "momentary";
}

export function buttonModeLabel(mode) {
    return normalizeButtonMode(mode) === "alternate" ? "オルタネート" : "モメンタリ";
}

export function ensureButtonNodeData(data = {}) {
    data.nodeType = "button";
    data.label = String(data.label || "押しボタン").trim() || "押しボタン";
    data.signalName = String(data.signalName || "X_PB1").trim() || "X_PB1";
    data.mode = normalizeButtonMode(data.mode);
    if (!data.trueLabel) data.trueLabel = "ON";
    if (!data.falseLabel) data.falseLabel = "OFF";
    return data;
}

export function renderStartNodeHtml(data) {
    return `
        ${renderNodeTypeLabel("開始")}
        <div class="node-name">${escapeHtml(data.label || "開始")}</div>
    `;
}

export function renderButtonNodeHtml(data) {
    const normalized = ensureButtonNodeData({ ...data });
    return `
        ${renderNodeTypeLabel("操作入力")}
        <div class="button-layout">
            <div class="node-name">${escapeHtml(normalized.label || "押しボタン")}</div>
            <div class="node-meta">${escapeHtml(normalized.signalName || "X_PB1")} / ${escapeHtml(buttonModeLabel(normalized.mode))}</div>
            <div class="node-branch">
                <div class="node-branch-item top">${escapeHtml(normalized.trueLabel || "ON")}</div>
                <div class="node-branch-item bottom">${escapeHtml(normalized.falseLabel || "OFF")}</div>
            </div>
        </div>
    `;
}

export function renderConditionNodeHtml(data) {
    const normalized = ensureConditionNodeData({ ...data });
    const metaText = buildConditionNodeMeta(normalized);
    return `
        ${renderNodeTypeLabel("条件分岐")}
        <div class="condition-layout">
            <div class="node-name">${escapeHtml(normalized.conditionText || "条件")}</div>
            ${metaText ? `<div class="node-meta">${escapeHtml(metaText)}</div>` : ""}
            <div class="node-branch">
                <div class="node-branch-item top">${escapeHtml(normalized.trueLabel || "ON")}</div>
                <div class="node-branch-item middle">${escapeHtml(normalized.falseLabel || "OFF")}</div>
                <div class="node-branch-item bottom">${escapeHtml((normalized.timeoutLabel || "TIMEOUT") + " " + (normalized.timeoutSec ?? 0) + "s")}</div>
            </div>
        </div>
    `;
}

export function renderErrorNodeHtml(data) {
    return `
        <div class="error-layout">
            ${renderNodeTypeLabel("エラー")}
            <div class="node-name">${escapeHtml(data.errorCode || "E001")}</div>
            <div class="node-meta">${escapeHtml(data.errorMessage || "エラー")}</div>
        </div>
    `;
}

export function renderSignalOutputNodeHtml(data) {
    const normalized = ensureSignalOutputData({ ...data });
    const metaPrefix = normalized.signalScope === "internal_flag" ? "内部フラグ" : "信号";
    return `
        <div class="signal-output-layout">
            ${renderNodeTypeLabel(normalized.signalScope === "internal_flag" ? "内部フラグ" : "信号操作")}
            <div class="node-name">${escapeHtml(normalized.label || "信号操作")}</div>
            <div class="node-meta">${escapeHtml(metaPrefix)} ${escapeHtml(normalized.signalName || "Signal")} := ${escapeHtml(normalized.action || "ON")}</div>
        </div>
    `;
}

export function renderWaitNodeHtml(data) {
    const normalized = ensureWaitNodeData({ ...data });
    return `
        <div class="signal-output-layout">
            ${renderNodeTypeLabel("待機")}
            <div class="node-name">${escapeHtml(normalized.label || "待機")}</div>
            <div class="node-meta">${escapeHtml((normalized.durationSec ?? 0).toFixed(2))}s</div>
        </div>
    `;
}

function renderSubprocessPreviewHtml(previewState = null) {
    if (!previewState || !Array.isArray(previewState.nodes) || !previewState.nodes.length) {
        return `
            <div class="subprocess-preview is-empty">
                <div class="subprocess-preview-empty">内部ノードなし</div>
            </div>
        `;
    }
    const canvasWidth = Math.max(120, numOrZero(previewState.canvasWidth || 0));
    const canvasHeight = Math.max(72, numOrZero(previewState.canvasHeight || 0));
    const edgeLines = (Array.isArray(previewState.edges) ? previewState.edges : []).map((edge) => `
        <line
            class="subprocess-preview-edge"
            x1="${escapeHtml(String(numOrZero(edge.x1).toFixed(1)))}"
            y1="${escapeHtml(String(numOrZero(edge.y1).toFixed(1)))}"
            x2="${escapeHtml(String(numOrZero(edge.x2).toFixed(1)))}"
            y2="${escapeHtml(String(numOrZero(edge.y2).toFixed(1)))}"
        ></line>
    `).join("");
    const previewItems = previewState.nodes.map((node) => `
        <div
            class="subprocess-preview-node node-${escapeHtml(node.type || "generic")}"
            style="left:${escapeHtml(String(numOrZero(node.left).toFixed(1)))}px;top:${escapeHtml(String(numOrZero(node.top).toFixed(1)))}px;width:${escapeHtml(String(numOrZero(node.width).toFixed(1)))}px;height:${escapeHtml(String(numOrZero(node.height).toFixed(1)))}px;"
            title="${escapeHtml(node.label || "ノード")}"
        >${escapeHtml(node.label || "ノード")}</div>
    `).join("");
    return `
        <div class="subprocess-preview" style="width:${escapeHtml(String(canvasWidth.toFixed(1)))}px;height:${escapeHtml(String(canvasHeight.toFixed(1)))}px;">
            <div class="subprocess-preview-group"></div>
            <svg class="subprocess-preview-svg" viewBox="0 0 ${escapeHtml(String(canvasWidth.toFixed(1)))} ${escapeHtml(String(canvasHeight.toFixed(1)))}" preserveAspectRatio="none" aria-hidden="true">
                ${edgeLines}
            </svg>
            ${previewItems}
        </div>
    `;
}

// subprocessList: injected by caller to avoid reading global state
export function renderSubprocessNodeHtml(data, subprocessList = [], previewState = null) {
    const meta = data?.subprocessId
        ? (subprocessList.find((s) => s.id === data.subprocessId) ?? null)
        : null;
    const subprocessName = meta?.name || data.subprocessName || data.label || "サブプロセス";
    const durationSec = Math.max(0, numOrZero(meta?.durationSec ?? data.durationSec ?? 1));
    const isCollapsed = Boolean(data?.collapsed);
    const collapsedLabel = "サブプロセス";
    const expandedLabel = `${subprocessName}${previewState?.nodes?.length ? ` - ${previewState.nodes.length} steps` : ""}`;
    return `
        <div class="subprocess-layout ${isCollapsed ? "is-collapsed" : "is-expanded"}" data-subprocess-collapsed="${isCollapsed ? "1" : "0"}">
            ${isCollapsed ? `
                <div class="subprocess-collapsed-shell">
                    <div class="subprocess-group-label" data-flow-node-action="toggle-subprocess-collapse">${escapeHtml(collapsedLabel)}</div>
                    <div class="subprocess-collapsed-body">
                        <div class="node-name">${escapeHtml(subprocessName)}</div>
                        <div class="node-meta subprocess-duration">固定時間 ${escapeHtml(durationSec.toFixed(2))}s</div>
                    </div>
                </div>
            ` : `
                <div class="subprocess-expanded-shell">
                    <div class="subprocess-expanded-body">
                        <div class="subprocess-group-label" data-flow-node-action="toggle-subprocess-collapse">${escapeHtml(expandedLabel)}</div>
                        ${renderSubprocessPreviewHtml(previewState)}
                    </div>
                </div>
            `}
        </div>
    `;
}

export function renderEndNodeHtml(data) {
    const resultLabel = data.result === "abort" ? "中断" : data.result === "error" ? "異常終了" : "正常終了";
    return `
        <div class="end-layout">
            ${renderNodeTypeLabel("終了")}
            <div class="node-name">${escapeHtml(data.label || "終了")}</div>
            <div class="node-meta">${escapeHtml(resultLabel)}</div>
        </div>
    `;
}

function getActuatorTypeLabel(actuatorType = "") {
    switch (String(actuatorType || "").trim()) {
        case "motor":
            return "モータ";
        case "chuck":
            return "チャック";
        case "vacuum":
            return "バキューム";
        case "heater":
            return "ヒータ";
        case "generic":
            return "汎用I/O";
        case "hydraulicCylinder":
            return "油圧シリンダ";
        case "electricCylinder":
            return "電動シリンダ";
        case "pneumaticCylinder":
        case "cylinder":
        default:
            return "空圧シリンダ";
    }
}

function getMotorControlTypeLabel(motorControlType = "") {
    switch (String(motorControlType || "").trim()) {
        case "pulseCommand":
            return "パルスモータ（コマンド入力）";
        case "pulseTrain":
            return "パルスモータ（パルス入力）";
        case "servo":
            return "サーボモータ";
        case "inverter":
            return "インバータ";
        case "speedControl":
        default:
            return "速度指定モータ";
    }
}

function getControlMethodLabel(controlMethod = "") {
    switch (String(controlMethod || "").trim()) {
        case "discreteIo":
        case "discreteIO":
            return "I/O";
        case "plcopenMotion":
            return "PLCopen";
        case "plcopenFluidPower":
            return "PLCopen FP";
        case "vendorFunction":
            return "メーカーFB";
        case "motionFb":
            return "FB";
        default:
            return String(controlMethod || "").trim();
    }
}

function getActionLabel(actionId = "") {
    switch (String(actionId || "").trim()) {
        case "extend":
            return "前進";
        case "retract":
            return "後退";
        case "up":
            return "上昇";
        case "down":
            return "下降";
        case "chuck":
            return "チャック";
        case "unchuck":
            return "アンチャック";
        case "pick":
            return "吸着";
        case "release":
            return "吸着破壊";
        case "forward":
            return "正転";
        case "reverse":
            return "逆転";
        case "stop":
            return "停止";
        case "home":
            return "原点復帰";
        case "position":
            return "位置決め";
        case "jog":
            return "JOG";
        case "start":
            return "起動";
        case "on":
            return "ON";
        case "off":
            return "OFF";
        default:
            return String(actionId || "").trim();
    }
}

function resolveMotionSolenoidDirectionPair(actionId = "") {
    const normalized = String(actionId || "").trim();
    if (normalized === "up" || normalized === "down") {
        return { positiveLabel: "上昇", negativeLabel: "下降" };
    }
    return { positiveLabel: "前進", negativeLabel: "後退" };
}

function firstCommandOutput(commandOutputs = []) {
    return Array.isArray(commandOutputs)
        ? String(commandOutputs.find((value) => String(value || "").trim()) || "").trim()
        : "";
}

function resolvePneumaticValveStateKey(actionId = "") {
    const normalized = String(actionId || "").trim();
    if (normalized === "extend" || normalized === "up") return "extend";
    if (normalized === "retract" || normalized === "down") return "retract";
    if (normalized === "stop") return "stop";
    if (normalized === "emergency") return "emergency";
    return "";
}

function formatValveStateBadge(state = "") {
    switch (String(state || "").trim()) {
        case "on":
            return "●ON";
        case "off":
            return "○OFF";
        case "hold":
            return "保持";
        default:
            return "";
    }
}

function formatValveOutputLine(label = "", output = "", state = "") {
    const parts = [String(label || "").trim(), String(output || "").trim(), formatValveStateBadge(state)].filter(Boolean);
    if (parts.length === 0) return "";
    return `<div>${escapeHtml(parts.join(" "))}</div>`;
}

function buildMotionSolenoidHtml(actuator = null, action = null, actionId = "") {
    if (!actuator || actuator?.actuatorType !== "pneumaticCylinder" || !actionId) return "";
    const directionPair = resolveMotionSolenoidDirectionPair(actionId);
    const command = action?.command || {};
    const setOutput = firstCommandOutput(command.setOutputs);
    const resetOutput = firstCommandOutput(command.resetOutputs);
    const valveMode = actuator?.pneumaticValveMode === "singleSolenoid" ? "singleSolenoid" : "doubleSolenoid";
    const config = actuator?.solenoidConfig && typeof actuator.solenoidConfig === "object" ? actuator.solenoidConfig : {};
    const valveModel = actuator?.valveModel && typeof actuator.valveModel === "object" ? actuator.valveModel : {};
    const stateKey = resolvePneumaticValveStateKey(actionId);
    const stateRow = stateKey && valveModel?.stateTable ? valveModel.stateTable[stateKey] || {} : {};

    if (valveMode === "singleSolenoid") {
        const singleOutput = String(valveModel.singleOutput || "").trim() || setOutput || resetOutput || String(config.singleOutput || "").trim();
        const activeDirection = String(
            valveModel.singleSolenoidActiveDirection
            || config.singleSolenoidActiveDirection
            || (setOutput ? "positive" : "negative")
        ).trim() === "negative"
            ? "negative"
            : "positive";
        const activeLabel = activeDirection === "negative" ? directionPair.negativeLabel : directionPair.positiveLabel;
        const passiveLabel = activeDirection === "negative" ? directionPair.positiveLabel : directionPair.negativeLabel;
        if (!singleOutput) {
            return `
                <div class="node-meta" style="font-size:0.78em; color:#0f5f3b; margin-top:4px; line-height:1.35;">
                    <div>弁モデル未設定</div>
                </div>
            `;
        }
        return `
            <div class="node-meta" style="font-size:0.78em; color:#0f5f3b; margin-top:4px; line-height:1.35;">
                ${formatValveOutputLine("", singleOutput, stateRow?.single || "")}
                <div>${escapeHtml(`通電=${activeLabel} / OFF=${passiveLabel}`)}</div>
            </div>
        `;
    }

    const positiveOutput = String(valveModel.positiveOutput || "").trim()
        || String(config.positiveOutput || "").trim()
        || (actionId === "extend" || actionId === "up" ? setOutput : resetOutput);
    const negativeOutput = String(valveModel.negativeOutput || "").trim()
        || String(config.negativeOutput || "").trim()
        || (actionId === "extend" || actionId === "up" ? resetOutput : setOutput);
    if (!positiveOutput && !negativeOutput) {
        return `
            <div class="node-meta" style="font-size:0.78em; color:#0f5f3b; margin-top:4px; line-height:1.35;">
                <div>弁モデル未設定</div>
            </div>
        `;
    }
    return `
        <div class="node-meta" style="font-size:0.78em; color:#0f5f3b; margin-top:4px; line-height:1.35;">
            ${formatValveOutputLine(`${directionPair.positiveLabel}側`, positiveOutput, stateRow?.positive || "")}
            ${formatValveOutputLine(`${directionPair.negativeLabel}側`, negativeOutput, stateRow?.negative || "")}
        </div>
    `;
}

// motionTimes / actuators: injected by caller to avoid reading global state
export function renderMotionNodeHtml(data, axis, motion, motionTimes = {}, actuators = []) {
    const actuator = Array.isArray(actuators)
        ? actuators.find((row) => row?.id && row.id === data?.actuatorId)
        : null;
    const action = Array.isArray(actuator?.actions)
        ? actuator.actions.find((row) => row?.id && row.id === data?.actionId)
        : null;
    const actuatorType = actuator?.actuatorType || axis?.actuatorType || "";
    const motorControlType = actuator?.motorControlType || axis?.motorControlType || "";
    const actuatorMeta = [];
    if (actuatorType) {
        actuatorMeta.push(getActuatorTypeLabel(actuatorType));
        if (actuator?.controlMethod) {
            actuatorMeta.push(getControlMethodLabel(actuator.controlMethod));
        }
        if (actuatorType === "motor" && motorControlType) {
            actuatorMeta.push(getMotorControlTypeLabel(motorControlType));
        }
    }
    if (action?.plcopen?.fbName) {
        actuatorMeta.push(action.plcopen.fbName);
    }
    if (data?.actionId) {
        actuatorMeta.push(action?.name || getActionLabel(data.actionId));
    }
    const actuatorText = actuatorMeta.join(" / ");
    const label = axis?.name ? `${axis.name} / 動作` : "動作";
    const solenoidHtml = buildMotionSolenoidHtml(actuator, action, data?.actionId || "");
    return `
        ${renderNodeTypeLabel(label)}
        <div class="node-name">${escapeHtml(motion?.name || "")}</div>
        ${actuatorText ? `<div class="node-meta" style="font-size:0.8em; color:#0056b3; margin-top:2px;">${actuatorText}</div>` : ""}
        ${solenoidHtml}
    `;
}

// opts.subprocessList: passed to renderSubprocessNodeHtml
// opts.motionTimes / opts.actuators: passed to renderMotionNodeHtml
export function buildFlowNodeHtml(data, axis, motion, opts = {}) {
    const { subprocessList = [], motionTimes = {}, actuators = [], subprocessPreview = null } = opts;
    const nodeType = getNodeTypeFromData(data);
    if (nodeType === "start") return renderStartNodeHtml(data);
    if (nodeType === "button") return renderButtonNodeHtml(data);
    if (nodeType === "condition") return renderConditionNodeHtml(data);
    if (nodeType === "error") return renderErrorNodeHtml(data);
    if (nodeType === "signal_output") return renderSignalOutputNodeHtml(data);
    if (nodeType === "wait") return renderWaitNodeHtml(data);
    if (nodeType === "subprocess") return renderSubprocessNodeHtml(data, subprocessList, subprocessPreview);
    if (nodeType === "end") return renderEndNodeHtml(data);
    return renderMotionNodeHtml(data, axis, motion, motionTimes, actuators);
}
